import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_STUDIO, GET_STUDIO_PUBLIC } from '../graphql/queries/queries';
import { GET_STUDIO_EXACT_LOCATION } from '../graphql/queries/bookingQueries';
import { useSelector } from 'react-redux';
import { getCloudFrontImageUrl } from '../util/cloudFrontUtils';
import { buildCoarseLocationText, getPublicStudioCoordinates } from '../util/locationPrivacy';

import awsConfig from '../config/aws-config';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const STUDIO_GALLERY_IMAGE_OPTIONS = {
  width: 1200,
  height: 900,
  fit: 'cover',
  quality: 82,
  format: 'webp'
};

// Get approximate coordinates from coarse public fields only.
const getApproximateCoordinates = async (city, studioLocation, country = 'Germany') => {
  try {
    // Build address string from available fields
    const addressParts = [];
    if (studioLocation) addressParts.push(studioLocation);
    if (city) addressParts.push(city);
    if (country) addressParts.push(country);
    
    const addressString = addressParts.join(', ');
    
    if (!addressString || !GOOGLE_API_KEY) {
      return null;
    }
    
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressString)}&key=${GOOGLE_API_KEY}`
    );
    
    const data = await response.json();
    
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      return {
        latitude: location.lat,
        longitude: location.lng,
        isApproximate: true // Flag to indicate this is approximate
      };
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to get approximate coordinates:', error);
    return null;
  }
};

export const useStudio = (id, options = {}) => {
  const authState = useSelector((state) => state?.auth || {});
  const authUser = authState?.user || {};
  const userId = authUser?.id || authUser?._id;
  const isAuthenticated = Boolean(authState?.isAuthenticated && userId);
  const authStatus = authState?.status;
  const isAuthChecking = authStatus === 'loading' || authStatus === 'idle';
  const { includeExact = false } = options;

  const shouldSkipStudioQuery = !id || isAuthChecking;
  const queryDocument = isAuthenticated ? GET_STUDIO : GET_STUDIO_PUBLIC;

  const { data, loading: queryLoading, error } = useQuery(queryDocument, {
    variables: { id },
    skip: shouldSkipStudioQuery,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const { data: ownerExactData } = useQuery(GET_STUDIO_EXACT_LOCATION, {
    variables: { studioID: id },
    skip: !id || !includeExact || !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  const [locationInfo, setLocationInfo] = useState({ postalCode: '', address: '' });
  const [approximateCoordinates, setApproximateCoordinates] = useState(null);
  const rawStudio = useMemo(() => {
    if (shouldSkipStudioQuery || (queryLoading && !data?.getStudio)) {
      return undefined;
    }

    if (data === undefined && !error) {
      return undefined;
    }

    if (!data?.getStudio) {
      return null;
    }

    return {
      ...data.getStudio,
      ...(includeExact ? ownerExactData?.getStudioExactLocation : {}),
    };
  }, [
    data,
    data?.getStudio,
    error,
    includeExact,
    ownerExactData?.getStudioExactLocation,
    queryLoading,
    shouldSkipStudioQuery,
  ]);

  // Start preloading images immediately when raw data arrives
  useEffect(() => {
    if (rawStudio?.images && Array.isArray(rawStudio.images)) {
      const rawImages = rawStudio.images;
      const addedLinks = new Set();
      const imageLoaders = [];

      const appendHeadLink = (link) => {
        document.head.appendChild(link);
        addedLinks.add(link);
        return link;
      };

      const removeHeadLink = (link) => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
        addedLinks.delete(link);
      };
      
      // Pre-connect to CloudFront for faster loading
      const preconnectLink = document.createElement('link');
      preconnectLink.rel = 'preconnect';
      preconnectLink.href = 'https://d23shqpev1klq3.cloudfront.net';
      preconnectLink.crossOrigin = 'anonymous';
      appendHeadLink(preconnectLink);
      
      // Preload first 5 images immediately with high priority
      rawImages.slice(0, 5).forEach((imageKey) => {
        if (!imageKey) return;
        
        try {
          // Process URL immediately for preloading - use same dimensions as processedStudio
          const imageUrl = getCloudFrontImageUrl(imageKey, STUDIO_GALLERY_IMAGE_OPTIONS);
          
          // Create high-priority preload link
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = imageUrl;
          link.fetchpriority = 'high';
          appendHeadLink(link);
          
          // Also start loading with Image object
          const img = new Image();
          img.fetchpriority = 'high';
          imageLoaders.push(img);
          
          // Cleanup on load/error
          const cleanup = () => {
            removeHeadLink(link);
          };
          img.onload = cleanup;
          img.onerror = cleanup;
          img.src = imageUrl;
          
        } catch (error) {
          try {
            // Fallback to S3 URL
            const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
            const fallbackUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
            
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = fallbackUrl;
            link.fetchpriority = 'high';
            appendHeadLink(link);
            
            const img = new Image();
            imageLoaders.push(img);
            const cleanup = () => {
              removeHeadLink(link);
            };
            img.onload = cleanup;
            img.onerror = cleanup;
            img.src = fallbackUrl;
          } catch (fallbackError) {
            console.warn('Failed to preload studio image:', fallbackError);
          }
        }
      });

      return () => {
        imageLoaders.forEach((img) => {
          img.onload = null;
          img.onerror = null;
        });
        addedLinks.forEach((link) => {
          if (document.head.contains(link)) {
            document.head.removeChild(link);
          }
        });
        addedLinks.clear();
      };
    }
  }, [rawStudio?.images]); // Start preloading as soon as raw data arrives

  const processedStudio = useMemo(() => {
    if (rawStudio === undefined) {
      return undefined;
    }

    if (!rawStudio) {
      return null;
    }
    
    const studio = { ...rawStudio };

    const paidItems = Array.isArray(studio?.paidEquipmentItems?.items)
      ? studio.paidEquipmentItems.items
      : [];
    studio.optionalBookableEquipment = paidItems.map((item) => ({
      id: item.id,
      category: item.category || "",
      nameModel: item.name || "",
      quantity: typeof item.quantity === "number" ? item.quantity : 1,
      pricePerPiece: typeof item.price === "number" ? item.price : 0,
      description: item.description || "",
      imageKey: item.imageKey || null,
      sortOrder: item.sortOrder ?? 0,
    }));

    studio.availabilitiesList = Array.isArray(studio?.availabilities?.items)
      ? studio.availabilities.items.filter(Boolean)
      : [];
    
    // Process studio images for display
    if (studio.images && Array.isArray(studio.images)) {
      studio.processedImages = studio.images.map(imageKey => {
        if (!imageKey) return '';
        
        try {
          const cloudFrontUrl = getCloudFrontImageUrl(imageKey, STUDIO_GALLERY_IMAGE_OPTIONS);
          
          return cloudFrontUrl;
        } catch (error) {
          try {
            const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
            return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } catch (fallbackError) {
            return '';
          }
        }
      });
      
      // Keep original images for editor usage
      studio.originalImages = [...studio.images];
      
      // Remove the processed images property but keep originalImages
      delete studio.images;
    } else {
      studio.processedImages = [];
      studio.originalImages = [];
      delete studio.images;
    }
    
    // Process user profile picture if available
    if (studio.User?.profilePicture) {
      let processedProfilePicture = "";
      
      try {
        processedProfilePicture = getCloudFrontImageUrl(studio.User.profilePicture, {
          width: 200,
          height: 200,
          fit: 'cover'
        });
      } catch (error) {
        processedProfilePicture = "";
      }
      
      // Create a new User object with all the original properties plus the processed image
      studio.User = {
        ...studio.User,
        processedProfilePicture
      };
    } else if (studio.User) {
      // If there's a User but no profile picture, use the default image
      studio.User = {
        ...studio.User,
        processedProfilePicture: null
      };
    }
    
    studio.hasImages = studio?.processedImages && studio?.processedImages?.length > 0;
    
    // Add location information from state
    studio.postalCode = locationInfo?.postalCode;
    studio.address = locationInfo?.address || buildCoarseLocationText(studio);
    
    const publicCoordinates = getPublicStudioCoordinates(studio) || approximateCoordinates;
    if (publicCoordinates) {
      studio.mapLatitude = publicCoordinates.latitude;
      studio.mapLongitude = publicCoordinates.longitude;
      studio.isApproximateLocation = true;
    }
    
    return studio;
  }, [rawStudio, locationInfo, approximateCoordinates]);

  // Handle location info updates in useEffect
  useEffect(() => {
    let cancelled = false;

    if (processedStudio) {
      const fallbackInfo = { 
        postalCode: '',
        address: buildCoarseLocationText(processedStudio)
      };
      setLocationInfo(fallbackInfo);
      
      if (!getPublicStudioCoordinates(processedStudio) && (processedStudio.city || processedStudio.studioLocation)) {
        getApproximateCoordinates(
          processedStudio.city,
          processedStudio.studioLocation,
          processedStudio.country || 'Germany'
        ).then(coords => {
          if (cancelled) return;
          if (coords) {
            setApproximateCoordinates(coords);
          }
        });
      }
    }
  }, [
    processedStudio?.approxLatitude,
    processedStudio?.approxLongitude,
    processedStudio?.postcode,
    processedStudio?.studioLocation,
    processedStudio?.city,
    processedStudio?.country,
  ]);

  const isWaitingForStudio = Boolean(id) && (
    shouldSkipStudioQuery ||
    queryLoading ||
    (data === undefined && !error)
  );
  const notFound = !isWaitingForStudio && data?.getStudio === null;

  return {
    studio: processedStudio,
    loading: isWaitingForStudio,
    error,
    notFound,
  };
};
